import React, { useState, useEffect } from 'react';
import { chevronBackOutline, eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { IonButton, IonContent, IonPage, useIonRouter, IonIcon } from '@ionic/react';
import SignupField from './SignupField';
import SixDigitInput from './SixDigitInput';

import '../../styles/authentication/ResetPassword.scss';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLongEnough, setIsLongEnough] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasLetter, setHasLetter] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [step, setStep] = useState('email');
  const [message, setMessage] = useState('');
  const [errorMsg, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const router = useIonRouter();

  const resetForm = () => {
    setEmail('');
    setCode('');
    setNewPassword('');
    setStep('email');
    setMessage('');
  };

  const navigateToSignIn = () => {
    router.push('/signin');
    resetForm();
  };

  const handlePhoneNumber = inputNumber => {
    if (!inputNumber) {
      return 'Phone number not available';
    }
    // eslint-disable-next-line prefer-template
    const cleaned = '' + inputNumber;
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (!match) {
      return inputNumber;
    }
    return `(${match[1]}) ${match[2]}****`;
  };

  const apiUrl = 'https://vhx3rygae9.execute-api.us-east-1.amazonaws.com/dev/auth';

  const postToApi = async body => {
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Information you provided is incorrect. Try again.');
      }

      return await response.json();
    } catch (error) {
      console.error('Error during API call', error);
      throw error;
    }
  };

  const handleResetPassword = async e => {
    e.preventDefault();
    setMessage('');
    if (step === 'reset' && newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    try {
      if (step === 'email') {
        const response = await postToApi({ action: 'forgot_password', email });
        const phoneDestination = response?.CodeDeliveryDetails?.Destination;
        const formattedPhone = handlePhoneNumber(phoneDestination);
        setFormattedPhoneNumber(formattedPhone);
        setMessage('A reset code has been sent to the phone number associated with your account.');
        setStep('reset');
        setError(false);
      } else if (step === 'reset') {
        await postToApi({ action: 'confirm_forgot_password', email, code, password: newPassword });
        setMessage('Your password has been reset successfully.');
        setStep('confirm');
        setError(true);
      }
    } catch (error) {
      setMessage(error.message);
      setError(true);
    }
  };

  const handleCodeInput = processedCode => {
    setCode(processedCode);
  };

  useEffect(() => {
    setIsLongEnough(newPassword.length >= 8);
    setHasNumber(/\d/.test(newPassword));
    setHasLetter(/[A-Z]/.test(newPassword));
    setPasswordsMatch(newPassword && confirmPassword && newPassword === confirmPassword);
  }, [newPassword, confirmPassword]);

  const getClassIfTrue = (condition, hasValue) => {
    if (!hasValue) return 'item';
    return condition ? 'item invisible' : 'item';
  };

  const hasUnmetRequirements = !isLongEnough || !hasNumber || !hasLetter || !passwordsMatch;

  return (
    <IonPage className='ion-page'>
      <IonContent id='reset-page'>
        <div className='reset-password-content'>
          <div className='signup-nav-header'>
            <IonButton className='signup-back-button' onClick={navigateToSignIn} fill='clear'>
              <IonIcon slot='icon-only' icon={chevronBackOutline} className='signup-back-icon' />
            </IonButton>
            <span className='signup-nav-title'>Reset Password</span>
          </div>
          <form id='entry-options' onSubmit={handleResetPassword}>
            {step === 'email' && (
              <>
                <SignupField label='Email' type='email' setter={setEmail} value={email} />
                <IonButton className='next-button' type='submit'>
                  Next
                </IonButton>
              </>
            )}

            {step === 'reset' && (
              <>
                <p className='verification-remark'>
                  We have sent the code to <br /> {formattedPhoneNumber}
                </p>
                <SixDigitInput onCodeChange={handleCodeInput} />
                <div
                  className={`signup-field-input-wrapper ${hasUnmetRequirements ? 'password-field-error' : ''}`}
                  style={{ position: 'relative' }}>
                  <SignupField
                    label='Password'
                    type={showPassword ? 'text' : 'password'}
                    setter={setNewPassword}
                    value={newPassword}
                  />
                  <IonIcon
                    className='reset-password-toggle-icon'
                    icon={showPassword ? eyeOffOutline : eyeOutline}
                    onClick={() => setShowPassword(v => !v)}
                  />
                </div>
                <div
                  className={`signup-field-input-wrapper ${hasUnmetRequirements ? 'password-field-error' : ''}`}
                  style={{ position: 'relative' }}>
                  <SignupField
                    label='Confirm Password'
                    type={showConfirmPassword ? 'text' : 'password'}
                    setter={setConfirmPassword}
                    value={confirmPassword}
                  />
                  <IonIcon
                    className='reset-password-toggle-icon'
                    icon={showConfirmPassword ? eyeOffOutline : eyeOutline}
                    onClick={() => setShowConfirmPassword(v => !v)}
                  />
                </div>
                <div className={`password-req${hasUnmetRequirements ? ' has-unmet-requirements' : ''}`}>
                  <ul>
                    <li id='password-capital' className={getClassIfTrue(hasLetter, newPassword.length > 0)}>
                      Password must have at least one capital letter
                    </li>
                    <li id='password-length' className={getClassIfTrue(isLongEnough, newPassword.length > 0)}>
                      Password must be at least 8 characters long
                    </li>
                    <li id='password-number' className={getClassIfTrue(hasNumber, newPassword.length > 0)}>
                      Password must contain at least one number
                    </li>
                    <li
                      id='password-match'
                      className={getClassIfTrue(passwordsMatch, newPassword.length > 0 && confirmPassword.length > 0)}>
                      Passwords must match
                    </li>
                  </ul>
                </div>
                <IonButton className='next-button' type='submit'>
                  Confirm
                </IonButton>
              </>
            )}

            {message && errorMsg && step === 'confirm' && <p className='reset-msg'> {message}</p>}
          </form>
          {message && errorMsg && step !== 'confirm' && <p className='reset-msg'>{message}</p>}
        </div>
        <div className='question-wrapper'>
          <p className='condition-text'>
            Still got an issue?
            <a className='contact-us-link' href='https://www.getfoodfight.com/'>
              Contact FoodFight
            </a>
          </p>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ResetPassword;
